import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { AlertModule } from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { FeatureFlagsService } from 'app';
import { AuthService } from 'auth';
import { TranslationModule } from 'i18n';
import { isAdmin as isAdminUser, Platform } from 'shared';
import { CountdownTimerComponent } from '../../../components/countdown-timer/countdown-timer.component';
import { OrderService } from '../../../services/order.service';
import { OrderWizardService, OrderWizardState } from '../../../services/order-wizard.service';
import { OrderSuccessComponent } from './steps/order-success/order-success.component';
import { EwalletConfirmationComponent } from './steps/payment-confirmation/ewallet-confirmation/ewallet-confirmation.component';
import { StripeConfirmationComponent } from './steps/payment-confirmation/stripe-confirmation/stripe-confirmation.component';
import { AuthorizeNetConfirmationComponent } from './steps/payment-confirmation/authorize-net-confirmation/authorize-net-confirmation.component';
import { Web3ConfirmationComponent } from './steps/payment-confirmation/web3-confirmation/web3-confirmation.component';
import { PaymentMethodComponent } from './steps/payment-method/payment-method.component';
import { ProductSelectionComponent } from './steps/product-selection/product-selection.component';
import { OrderWizardTracking } from './order-wizard-tracking';
import { TrackingCheckoutStep } from 'analytics';

/** Payment methods this wizard renders a confirmation screen for (see the template). */
const CONFIRMATION_METHODS = ['ewallet', 'credit_card', 'authorize_net', 'web3_payment'];

@Component({
  selector: 'app-order-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ProductSelectionComponent,
    PaymentMethodComponent,
    EwalletConfirmationComponent,
    StripeConfirmationComponent,
    AuthorizeNetConfirmationComponent,
    Web3ConfirmationComponent,
    OrderSuccessComponent,
    CountdownTimerComponent,
    TranslationModule,
    AlertModule,
    IconDirective
  ],
  templateUrl: './order-wizard.component.html',
  styleUrls: ['./order-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Tracking Engine reports; silent unless the app provides TRACKING_CHECKOUT_CONFIG.
  providers: [OrderWizardTracking]
})
export class OrderWizardComponent {
  private wizardService = inject(OrderWizardService);
  private featureFlags = inject(FeatureFlagsService);
  private auth = inject(AuthService);
  private platform = inject(Platform);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrderService);
  private deepLinkHandled = false;
  private tracking = inject(OrderWizardTracking);
  /** A `?add=` deep link is still resolving: step 1 is only a flash, not a screen the buyer saw. */
  private deepLinkPending = signal(!!Number(this.route.snapshot.queryParamMap.get('add')));
  /** Last screen a `view` was sent for — the effect re-runs on unrelated signal changes. */
  private lastViewKey: string | null = null;

  // ────────────────────────────────────────────────
  // Reactive state from wizard service
  // ────────────────────────────────────────────────
  state = toSignal(this.wizardService.state$, {initialValue: this.wizardService.getState()});

  // Derived signals
  currentStep = computed(() => this.state().currentStep);
  paymentMethod = computed(() => this.state().selectedPaymentMethod);
  orderId = computed(() => this.state().orderId);
  totalPrice = computed(() => this.state().totalPrice);
  paymentCosts = computed(() => this.state().paymentCosts);
  totalWithCosts = computed(() => this.state().totalWithCosts);
  isFirstOrder = computed(() => this.state().isFirstOrder);
  recipientAddress = computed(() => this.state().recipientAddress || '');
  // An order is single-currency; derive it from the cart products.
  currency = computed(() => this.state().cart[0]?.product?.currency || 'USDT');

  // Feature & role checks
  isAdmin = computed(() => isAdminUser(this.auth.getUser()));
  ordersDisabled = computed(() => !this.featureFlags.isOrdersEnabled() && !this.isAdmin());

  // Countdown target (November 15th, 2025, 23:59:59 Atlanta time)
  orderStartDate = new Date('2025-11-15T23:59:59-05:00');

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    // Sync state changes
    // effect(() => {
      // this.wizardService.state$
      //     .pipe(takeUntilDestroyed(this.destroyRef))
      //     .subscribe(s => {
      //       this.state.set(s);
      //     });
    // });

    // Reset wizard on mount
    effect(() => {
      this.wizardService.reset();
    });

    // Deep-link: /order-wizard?add=<productId>&step=<n> preselects a product and
    // jumps to the given step (default 2 = payment). Registered AFTER the reset
    // effect so the pre-seeded cart survives it. Uses getProduct(id) (not the
    // language-filtered product list) so the link works regardless of the user's
    // profile language. Generic for every app.
    effect(() => {
      if (this.deepLinkHandled) return;
      const params = this.route.snapshot.queryParamMap;
      const addId = Number(params.get('add'));
      if (!addId) return;
      this.deepLinkHandled = true;
      const step = Number(params.get('step')) || 2;
      this.orderService.getProduct(addId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: product => {
            this.tracking.started([product?.code]);
            this.deepLinkPending.set(false);
            if (!product) return;
            this.wizardService.addToCart(product, 1);
            this.wizardService.goToStep(step);
          },
          error: () => {
            this.tracking.started([]);
            this.deepLinkPending.set(false);
          }
        });
    });

    // Tracking Engine: one `checkout_step … view` per screen shown (also on browser
    // back/forward), so the drop-off point — last step without `success` — is known.
    effect(() => {
      const step = this.currentStep();
      const method = this.paymentMethod();
      if (step === 1 && this.deepLinkPending()) return;
      let tracked: TrackingCheckoutStep | null = null;
      if (step === 1) tracked = 'plan';
      else if (step === 2) tracked = 'payment_method';
      else if (step === 3 && method && CONFIRMATION_METHODS.includes(method)) tracked = 'payment_confirmation';
      else if (step === 4) tracked = 'success';
      const key = tracked ? `${step}|${step === 3 ? method : ''}` : null;
      if (!tracked || key === this.lastViewKey) return;
      this.lastViewKey = key;
      this.tracking.step(tracked, 'view');
    });

    // Enable history management
    effect(() => {
      const basePath = window.location.pathname.split('?')[0];
      this.wizardService.enableHistoryManagement(basePath);
    });

    // Cleanup on destroy
    effect(() => {
      return () => {
        this.wizardService.disableHistoryManagement();
      };
    });
  }

  // ────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────
  handleProductSelection(): void {
    this.wizardService.nextStep();
  }

  handlePaymentMethodSelection(): void {
    this.wizardService.nextStep();
  }

  handlePaymentSuccess(event: { isFirstOrder?: boolean }): void {
    if (event.isFirstOrder !== undefined) {
      this.wizardService.setFirstOrder(event.isFirstOrder);
    }
    this.wizardService.nextStep();
  }

  handleBack(step: number): void {
    this.wizardService.goToStep(step);
  }
}